import React, { useState, useEffect } from 'react';
import Button from '../../components/base/Button';
import { Form } from 'react-bootstrap';

interface FormData {
  name: string;
  email: string;
  message: string;
}

interface SubmitStatus {
  show: boolean;
  type: string;
  message: string;
}

interface FooterLink {
  text: string;
  url: string;
}

interface ContactInfoItem {
  icon: string;
  title: string;
  description: string;
  link: string;
}

interface RecentPost {
  image: string;
  date: string;
  title: string;
  link: string;
}

interface KidsaFooterProps {
  logo?: string;
  newsletterText?: string;
  quickLinks?: FooterLink[];
  categories?: FooterLink[];
  contactInfo?: ContactInfoItem[];
  recentPosts?: RecentPost[];
  copyrightText?: string;
  termsLink?: string;
  privacyLink?: string;
  socialLinks?: {
    facebook?: string;
    twitter?: string;
    linkedin?: string;
    youtube?: string;
  };
}

const KidsaFooter: React.FC<KidsaFooterProps> = ({
  logo,
  newsletterText,
  quickLinks,
  categories,
  contactInfo,
  recentPosts,
  copyrightText,
  termsLink,
  privacyLink,
  socialLinks
}) => {
    const [formData, setFormData] = useState<FormData>({
      name: '',
      email: '',
      message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [submitStatus, setSubmitStatus] = useState<SubmitStatus>({ 
      show: false, 
      type: '', 
      message: '' 
    });
    const [inputsDisabled, setInputsDisabled] = useState<boolean>(false);
  
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    };
  
    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setIsSubmitting(true);
      setSubmitStatus({ show: false, type: '', message: '' });
  
      try {
        const response = await fetch('https://backend.anubisai.net/api/mail/contact', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData)
        });
  
        const data = await response.json();
  
        if (response.ok) {
          setSubmitStatus({
            show: true,
            type: 'success',
            message: 'Hemos recibido su mensaje!'
          });
          setFormData({ name: '', email: '', message: '' });
          
          setInputsDisabled(true);
          setIsSubmitting(false);
          
          // Después de 20 segundos, desvanecer mensaje y desbloquear inputs
          setTimeout(() => {
            setSubmitStatus({ show: false, type: '', message: '' });
            setInputsDisabled(false);
          }, 20000);
          
        } else {
          throw new Error(data.message || 'Failed to send message');
        }
      } catch (err: unknown) {
        let errorMessage = 'An error occurred while sending the message';
        
        if (err instanceof Error) {
          errorMessage = err.message;
        } else if (typeof err === 'string') {
          errorMessage = err;
        } else if (err && typeof err === 'object' && 'message' in err) {
          errorMessage = String((err as { message: unknown }).message);
        }
        
        setSubmitStatus({
          show: true,
          type: 'error',
          message: errorMessage
        });
        setIsSubmitting(false);
      }
    };

    // Cleanup effect to clear timeout if component unmounts
    useEffect(() => {
      return () => {
        // Clear any pending timeouts when component unmounts
        if (submitStatus.show && submitStatus.type === 'success') {
          setInputsDisabled(false);
        }
      };
    }, [submitStatus]);

      useEffect(() => {
        const scrollToHash = () => {
          const hash = window.location.hash;
          if (hash) {
            const id = hash.substring(1);
            const element = document.getElementById(id);
            
            if (element) {
              element.scrollIntoView({ 
                behavior: 'smooth',
                block: 'start'
              });
            }
          }
        };
    
        scrollToHash();
        const handleHashChange = () => {
          scrollToHash();
        };
    
        window.addEventListener('hashchange', handleHashChange);
        return () => {
          window.removeEventListener('hashchange', handleHashChange);
        };
      }, []);

  return (
    <footer id="" className="footer-section section-bg fix">
      <div className="footer-top-shape">
        <img src="/assets/kidsa/assets/img/footer-top.png" alt="shape-img" />
      </div>
      <div className="frame-shape">
        <img src="/assets/kidsa/assets/img/frame.png" alt="shape-img" />
      </div>
      <div className="zebra-shape float-bob-y">
        <img src="/assets/kidsa/assets/img/line-2.png" alt="shape-img" />
      </div>
      {/*<div className="container">
        <div className="contact-info-area">
          {contactInfo?.map((info, index) => (
            <div 
              key={index} 
              className="contact-info-items wow fadeInUp" 
              data-wow-delay={`.${(index + 3) * 0.1}s`}
            >
              <div className="icon">
                <i className={info.icon}></i>
              </div>
              <div className="containerx">
                <p>{info.title}</p>
                <h3 className="text-reset text-lowercase">
                  <a href={info.link}>{info.description}</a>
                </h3>
              </div>
            </div>
          ))}
        </div>
      </div>*/}
      <div className="footer-widgets-wrapper">
        <div className="container">
          <div className="row">
            <div className="col-xl-3 col-lg-4 col-md-6 wow fadeInUp" data-wow-delay=".3s">
              <div className="single-footer-widget">
                <div className="widget-head">
                  <a href="/">
                    <img src={logo} width="180" alt="logo-img" />
                  </a>
                </div>
                <div className="footer-content">
                  <p>{newsletterText}</p>
                  <div className="social-icon d-flex align-items-center">
                    {Object.entries(socialLinks || {}).map(([platform, url]) => (
                      <a key={platform} href={url}>
                        <i className={`fab fa-${platform}`}></i>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-2 col-lg-4 col-md-6 ps-lg-5 wow fadeInUp" data-wow-delay=".5s">
              <div className="single-footer-widget">
                <div className="widget-head">
                  <h3>Colegio</h3>
                </div>
                <ul className="list-area">
                  {quickLinks?.map((link, index) => (
                    <li key={index}>
                      <a href={link.url}>
                        <i className="fa-solid fa-chevron-right"></i>
                        {link.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="col-xl-3 col-lg-4 col-md-6 ps-lg-5 wow fadeInUp" data-wow-delay=".5s">
              <div className="single-footer-widget style-margin">
                <div className="widget-head">
                  <h3>Comunidad</h3>
                </div>
                <ul className="list-area">
                  {categories?.map((category, index) => (
                    <li key={index}>
                      <a href={category.url}>
                        <i className="fa-solid fa-chevron-right"></i>
                        {category.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="col-xl-4 col-lg-4 col-md-6 wow fadeInUp" id="contacto" data-wow-delay=".7s">
              <div className="single-footer-widget style-margin">
                <div className="widget-head">
                  <h3>Contacto</h3>
                </div>
                <div className="recent-post-area">
                  {submitStatus.show && (
                          <div className={`alert ${submitStatus.type === 'success' ? 'alert-success' : 'alert-danger'} mb-4`}>
                            {submitStatus.message}
                          </div>
                        )}
                  
                        <Form className="d-flex wow flex-column gap-4" onSubmit={handleSubmit}>
                          <Form.Control
                            type="text"
                            name="name"
                            placeholder="Nombre"
                            required
                            value={formData.name}
                            onChange={handleChange}
                            disabled={isSubmitting || inputsDisabled}
                          />
                          <Form.Control
                            type="email"
                            name="email"
                            placeholder="Email"
                            required
                            value={formData.email}
                            onChange={handleChange}
                            disabled={isSubmitting || inputsDisabled}
                          />
                          <Form.Control
                            as="textarea"
                            rows={6}
                            name="message"
                            placeholder="Mensaje"
                            required
                            value={formData.message}
                            onChange={handleChange}
                            disabled={isSubmitting || inputsDisabled}
                          />
                          <Button 
                            type="submit" 
                            variant="outline-danger"
                            className="theme-btn"
                            disabled={isSubmitting || inputsDisabled}
                          >
                            {isSubmitting ? 'Enviando...' : inputsDisabled ? 'Mensaje enviado' : 'Contáctanos'}
                          </Button>
                        </Form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="footer-bottom">
        <div className="f-bottom-shape">
          <img src="/assets/kidsa/assets/img/footer-bottom.png" alt="shape-img" />
        </div>
        <div className="container">
          <div className="footer-wrapper d-flex align-items-center justify-content-between">
            <p 
              className="wow fadeInLeft color-1" 
              data-wow-delay=".3s"
              dangerouslySetInnerHTML={{ __html: copyrightText || '' }}
            />
            <ul className="footer-menu wow fadeInRight" data-wow-delay=".5s">
              <li>
                <a href={termsLink}>
                  Términos & Condiciones
                </a>
              </li>
              <li>
                <a href={privacyLink}>
                  Política de Privacidad
                </a>
              </li>
            </ul>
          </div>
        </div>
        <a href="#" id="scrollUp" className="scroll-icon">
          <i className="far fa-arrow-up"></i>
        </a>
      </div>
    </footer>
  );
};

export default KidsaFooter;